import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { normalizeLang } from "@/lib/i18n";
import { articlesForScreen, currentScreen, helpContent, searchArticles, splitLinks } from "@/lib/help";

// The help panel (HELP_SYSTEM_PLAN.md, phase 1): opens from the Help button on the
// right, leads with the articles for the screen the candidate is on, then search and
// every topic. The screen behind keeps its state.
export const UI = {
  en: { help: "Help", close: "Close help", search: "Search help", forScreen: "For this screen", all: "Browse all topics", back: "Back", none: "No articles match your search.", results: "Results", english: "This article is shown in English while its translation is being prepared." },
  "pt-BR": { help: "Ajuda", close: "Fechar a ajuda", search: "Pesquisar na ajuda", forScreen: "Para esta tela", all: "Ver todos os tópicos", back: "Voltar", none: "Nenhum artigo corresponde à sua pesquisa.", results: "Resultados", english: "Este artigo está em inglês enquanto a tradução é preparada." },
  "es-419": { help: "Ayuda", close: "Cerrar la ayuda", search: "Buscar en la ayuda", forScreen: "Para esta pantalla", all: "Ver todos los temas", back: "Volver", none: "Ningún artículo coincide con su búsqueda.", results: "Resultados", english: "Este artículo se muestra en inglés mientras se prepara la traducción." },
  "fr-CA": { help: "Aide", close: "Fermer l’aide", search: "Rechercher dans l’aide", forScreen: "Pour cet écran", all: "Parcourir tous les sujets", back: "Retour", none: "Aucun article ne correspond à votre recherche.", results: "Résultats", english: "Cet article est affiché en anglais pendant la préparation de sa traduction." },
};

// One line of an article, with its links to other articles.
function Line({ text, onOpen }) {
  return splitLinks(text).map((part, i) => (part.articleId ? (
    <button key={i} type="button" onClick={() => onOpen(part.articleId)}
      className="inline p-0 text-left font-medium text-[color:var(--brand)] underline underline-offset-2 hover:no-underline"
      data-testid={`help-inline-link-${part.articleId}`}>
      {part.text}
    </button>
  ) : <React.Fragment key={i}>{part.text}</React.Fragment>));
}

function ArticleBody({ article, onOpen }) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-[color:var(--text-primary)]">
      {article.body.map((b, i) => {
        if (b.p) return <p key={i}><Line text={b.p} onOpen={onOpen} /></p>;
        if (b.note) return <p key={i} className="rounded-lg bg-[color:var(--brand)]/5 border border-[color:var(--brand)]/15 px-3 py-2"><Line text={b.note} onOpen={onOpen} /></p>;
        if (b.steps) return <ol key={i} className="list-decimal pl-5 space-y-1.5">{b.steps.map((s, j) => <li key={j}><Line text={s} onOpen={onOpen} /></li>)}</ol>;
        if (b.list) return <ul key={i} className="list-disc pl-5 space-y-1.5">{b.list.map((s, j) => <li key={j}><Line text={s} onOpen={onOpen} /></li>)}</ul>;
        return null;
      })}
    </div>
  );
}

function ArticleLink({ article, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(article.id)}
      className="block w-full text-left rounded-lg px-3 py-3 sm:py-2 text-sm text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--brand)]"
      data-testid={`help-article-link-${article.id}`}>
      {article.title}
    </button>
  );
}

export default function HelpPanel() {
  const { user } = useAuth();
  const location = useLocation();
  const lang = normalizeLang(user?.preferred_language);
  const t = UI[lang] || UI.en;
  const { articles, categories, translated } = useMemo(() => helpContent(lang), [lang]);
  const [open, setOpen] = useState(false);
  const [articleId, setArticleId] = useState(null);
  const [query, setQuery] = useState("");
  const [screen, setScreen] = useState("general");
  const searchRef = useRef(null);
  const returnFocus = useRef(null);

  useEffect(() => {
    const onOpen = (e) => {
      returnFocus.current = document.activeElement;
      setScreen(currentScreen(window.location.pathname));
      setArticleId(e.detail?.articleId || null);
      setQuery("");
      setOpen(true);
    };
    window.addEventListener("mec:open-help", onOpen);
    return () => window.removeEventListener("mec:open-help", onOpen);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    // On a touch screen, focusing the search box would open the keyboard over the panel
    // as soon as Help opens: there it waits for a tap.
    const touch = window.matchMedia?.("(pointer: coarse)").matches;
    if (!articleId && !touch) searchRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, articleId]);

  // Phones (2026-10-31): the panel is full screen, so the page behind doesn't scroll, and
  // the phone's Back button closes Help instead of leaving the page.
  const pushedRef = useRef(false);
  useEffect(() => {
    if (!open || !window.matchMedia?.("(max-width: 639px)").matches) return undefined;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.history.pushState({ ...(window.history.state || {}), mecHelp: true }, "");
    pushedRef.current = true;
    const onPop = () => { pushedRef.current = false; setOpen(false); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      document.body.style.overflow = overflow;
      // Step back only over Help's own history entry, never over a real page.
      if (pushedRef.current && window.history.state?.mecHelp) window.history.back();
      pushedRef.current = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open && returnFocus.current) {
      returnFocus.current.focus?.();
      returnFocus.current = null;
    }
  }, [open]);

  if (!open) return null;
  const article = articleId ? articles.find((a) => a.id === articleId) : null;
  // Following a link inside an article: the new article, from its top.
  const openArticle = (id) => {
    setArticleId(id);
    document.querySelector('[data-testid="help-panel"] .overflow-y-auto')?.scrollTo?.(0, 0);
  };
  const results = query.trim() ? searchArticles(articles, query) : null;
  const forScreen = articlesForScreen(articles, screen);
  const related = article ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3) : [];

  return (
    <aside
      role="dialog"
      aria-label={t.help}
      className="fixed inset-y-0 right-0 z-[90] flex w-full flex-col border-l border-[color:var(--border-stone)] bg-white shadow-2xl sm:w-[420px]"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="help-panel"
    >
      <div className="flex items-center justify-between border-b border-[color:var(--border-stone)] px-5 py-4">
        {article ? (
          <button type="button" onClick={() => setArticleId(null)} className="inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--brand)]" data-testid="help-back">
            <ArrowLeft size={16} /> {t.back}
          </button>
        ) : (
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{t.help}</h2>
        )}
        <button type="button" onClick={() => setOpen(false)} aria-label={t.close} title={t.close}
          className="rounded-full p-1.5 text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-surface-hover)]" data-testid="help-close">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        {article ? (
          <article data-testid={`help-article-${article.id}`}>
            <h3 className="text-xl font-semibold leading-snug text-[color:var(--text-primary)]">{article.title}</h3>
            {!translated && lang !== "en" && <p className="mt-2 text-xs text-[color:var(--text-secondary)]">{t.english}</p>}
            <div className="mt-4"><ArticleBody article={article} onOpen={openArticle} /></div>
            {related.length > 0 && (
              <div className="mt-8 border-t border-[color:var(--border-stone)] pt-4">
                {related.map((a) => <ArticleLink key={a.id} article={a} onOpen={setArticleId} />)}
              </div>
            )}
          </article>
        ) : (
          <div className="space-y-6">
            <label className="relative block">
              <span className="sr-only">{t.search}</span>
              <MagnifyingGlass size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]" />
              <input
                ref={searchRef}
                id="help-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.search}
                className="w-full rounded-full border border-[color:var(--border-stone)] py-2 pl-9 pr-4 text-sm"
                data-testid="help-search"
              />
            </label>

            {results ? (
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--text-secondary)]">{t.results}</h3>
                {results.length ? results.map((a) => <ArticleLink key={a.id} article={a} onOpen={setArticleId} />)
                  : <p className="px-3 text-sm text-[color:var(--text-secondary)]">{t.none}</p>}
              </section>
            ) : (
              <>
                <section data-testid="help-for-screen">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--brand)]">{t.forScreen}</h3>
                  {forScreen.map((a) => <ArticleLink key={a.id} article={a} onOpen={setArticleId} />)}
                </section>
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--text-secondary)]">{t.all}</h3>
                  {categories.map((c) => (
                    <details key={c.id} className="group rounded-lg">
                      <summary className="cursor-pointer list-none rounded-lg px-3 py-3 sm:py-2 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]">
                        {c.title}
                      </summary>
                      <div className="pb-2 pl-3">
                        {articles.filter((a) => a.category === c.id).map((a) => <ArticleLink key={a.id} article={a} onOpen={setArticleId} />)}
                      </div>
                    </details>
                  ))}
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
